import {
  LEGAL_ENTITY,
  LEGAL_ENTITY_SHORT,
  LEGAL_ADDRESS,
  CONTACT_PRIVACY,
  CONTACT_SUPPORT,
  LEGAL_LAST_UPDATED,
  LEGAL_EFFECTIVE_DATE,
} from '../../config/legal';

# Privacy Policy

**Last updated:** {LEGAL_LAST_UPDATED}
**Effective:** {LEGAL_EFFECTIVE_DATE}

This Privacy Policy explains how {LEGAL_ENTITY} ("{LEGAL_ENTITY_SHORT}," "we," "us," or "our") collects, uses, shares, and protects personal information in connection with **ProgramRow**, our crew scheduling service at [programrow.com](https://programrow.com) (the "Service").

Please read it alongside our [Terms of Service](/terms).

---

## 1. Mobile information and text messages

We are placing this section first because it is the one people most often need.

**No mobile information will be shared with third parties or affiliates for marketing or promotional purposes.** Information sharing to subcontractors in support of services, such as customer service, is permitted. All other use case categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.

More plainly:

- We send text messages only to people who have **opted in themselves**, from their own ProgramRow account, and only after confirming the number by one-time passcode. An organization administrator cannot turn text messaging on for you.
- We text you only about **your own work**: crew assignments you have been given, changes to the time, date, or venue of events you are assigned to, cancellations, and operational messages from an organization you belong to.
- We never send you marketing or promotional texts, and we never let anyone else text you using your number.
- We do not sell phone numbers. We do not rent them, trade them, or hand them to data brokers, lead generators, or advertisers.
- **You can stop at any time** by replying **STOP** to any message, or by removing your number in Profile → Notifications. Reply **HELP** for help. Message frequency varies with how often you are assigned. Message and data rates may apply.

Turning off text messages never affects your account, your assignments, or your ability to use the Service. Email notification remains available, and text messaging is entirely optional.

When you opt in, we record the date and time, the IP address the consent came from, and the exact wording of the disclosure you agreed to. We keep that record so we can demonstrate, to you or to a mobile carrier, that the consent was real.

---

## 2. Who this policy covers, and our two different roles

ProgramRow is used by organizations (schools, colleges, athletic departments, and video production companies) to schedule the crews who work their events. That creates two different relationships, and your rights differ slightly depending on which one applies.

**When we act on our own behalf (as a "controller").** Your account itself, meaning your name, email address, password, login history, notification preferences, and any phone number you choose to add, is information we hold in order to run the Service and keep your account secure. You deal with us directly about it.

**When we act on an organization's behalf (as a "processor" or "service provider").** The working data inside an organization's workspace, meaning its events, its rosters, who was assigned to what, pay rates and payroll records, uploaded documents, and messages sent to crew, is controlled by that organization, not by us. We process it under that organization's instructions and under our agreement with them.

If you are a crew member and want data in an organization's workspace corrected or removed, the fastest route is that organization's administrator. Contact us at [{CONTACT_PRIVACY}](mailto:{CONTACT_PRIVACY}) and we will help route the request, but the organization decides what stays in its own records.

---

## 3. Information we collect

### 3.1 Information you give us

| What | When | Why |
|---|---|---|
| Name and email address | Creating an account, or being invited to one | Identify you, sign you in, send notifications |
| Password | Creating an account | Authentication (stored only as a bcrypt hash, which we cannot read) |
| Mobile phone number | Only if you choose to add one | Text notifications you opted into; never required |
| Profile photo | Optional | Displayed to your organization |
| Organization name, URL, timezone | Creating an organization | Set up your workspace |
| Payment and billing details | Purchasing a paid plan | Handled by Stripe; see section 5 |
| Feedback and support messages | When you contact us | Answer you and fix problems |

### 3.2 Information an organization gives us about you

An organization administrator may create an account for you, or import you along with other crew members, providing your name, email address, and sometimes a phone number and pay rate. If they do, you will receive an invitation to set your own password and take control of the account.

**A phone number entered by an administrator is not used for text messaging.** It is stored as contact information only. Text messaging requires your own opt-in as described in section 1.

### 3.3 Information collected automatically

- **Log data.** IP address, browser type and version, pages requested, response status, and timestamps. Used to operate the Service, diagnose errors, and detect abuse.
- **Organization activity logs.** A record of actions taken inside an organization's workspace (who created, changed, or deleted what, and when), available to that organization's owner.
- **Authentication cookies.** Required to keep you signed in.

**We do not use advertising cookies, third-party analytics trackers, or cross-site tracking of any kind.** Because we do not track you across sites, there is nothing for a Global Privacy Control or "Do Not Track" signal to turn off, but we honor them where they apply.

### 3.4 Google Sign-In

If you sign in with Google, Google tells us your name, email address, and whether that email is verified. We do not receive your Google password and we request no access to your Gmail, Drive, Calendar, or contacts.

### 3.5 Children

ProgramRow is designed for adults doing paid or volunteer production work, and accounts are intended for people **18 or older**.

We recognize that high school and college broadcast crews sometimes include people under 18, and that an organization may add a minor as a crew member. If an organization does so, that organization is responsible for obtaining any consent that applicable law requires from the student's parent or guardian, and for confirming it has the authority to share that student's information with us.

**We do not knowingly collect personal information from children under 13.** If we learn that we hold information from a child under 13 without the consent the law requires, we will delete it promptly. A parent or guardian who believes we hold such information should write to [{CONTACT_PRIVACY}](mailto:{CONTACT_PRIVACY}) and we will act on it.

We recommend organizations not enable text messaging for crew members under 18.

---

## 4. How we use information

We use personal information to:

- Provide the Service: create and secure accounts, show you your assignments, let organizations schedule and message their crews.
- Send **transactional** communications: account invitations, password resets, email verification, assignment notifications, schedule changes, and messages an organization's administrator sends to its own crew.
- Send **text messages** you have opted into, as described in section 1.
- Process payments and manage subscriptions.
- Keep the Service secure: detect and prevent fraud, abuse, credential stuffing, and unauthorized access.
- Diagnose problems and improve the Service, including reviewing error logs and any feedback you submit.
- Comply with law and enforce our [Terms of Service](/terms).

**We do not use your personal information to train machine learning models, and we do not sell or share it for behavioral advertising.**

We use automated systems to monitor error rates, service health, and abuse signals. These do not make decisions that produce legal or similarly significant effects about you.

---

## 5. How we share information

We share personal information only in the circumstances below.

### 5.1 Within your organization

Your name, email address, role, assignments, availability, and (if the organization has payment tracking enabled) your pay rates are visible to administrators and owners of organizations you belong to. Other crew members may see your name and role on shared events.

If you belong to more than one organization, **each one sees only its own workspace.** An organization cannot see your assignments, rates, or activity in any other organization.

### 5.2 Service providers

We use a small number of vendors to run the Service. Each is bound by contract to use the information only to provide their service to us:

| Provider | Purpose | Data involved |
|---|---|---|
| Google Cloud Platform | Hosting, database, file storage, logging | All Service data, stored in the United States |
| Postmark | Transactional email delivery | Recipient name, email address, message content |
| Twilio | Text message delivery and phone verification | Mobile number, message content |
| Stripe | Payment processing and subscription billing | Billing contact and payment details; card numbers go to Stripe directly and never touch our servers |
| Cloudflare | DNS, bot protection on public forms | IP address, request metadata |

### 5.3 Legal and safety

We may disclose information if we reasonably believe it is required by law, subpoena, or other legal process, or necessary to protect the rights, property, or safety of {LEGAL_ENTITY_SHORT}, our users, or the public. Where we are legally permitted to tell you first, we will.

### 5.4 Business transfers

If {LEGAL_ENTITY_SHORT} is involved in a merger, acquisition, or sale of assets, personal information may transfer as part of that transaction. We will give notice before your information becomes subject to a materially different privacy policy.

### 5.5 What we never do

**We do not sell personal information, and we have not sold or shared personal information for cross-context behavioral advertising in the preceding twelve months.** We do not disclose your information to advertisers, data brokers, or lead generators. Mobile phone numbers and text-message consent are never shared with anyone for marketing purposes, by any party, in any circumstance.

---

## 6. Data retention

- **Account information.** Kept while your account is active. If you delete your account, we delete or anonymize your personal information within 90 days, except where we must keep it longer for legal, tax, or fraud-prevention reasons.
- **Organization data.** Kept while the organization's account is active, and for 30 days after termination so it can be exported, then deleted. Organizations may set shorter retention by asking us.
- **Text message consent records.** Kept for as long as you are opted in, and for **at least four years** after opt-out. Mobile carriers and regulators can require proof of consent for messages already sent; keeping the record protects both of us.
- **Server logs.** Up to 90 days, then deleted.
- **Backups.** Up to 35 days, after which they age out. Deleted data may persist in backups until then.

---

## 7. Security

We protect information with, among other measures: TLS encryption in transit; encryption at rest for the database, backups, and uploaded files; bcrypt password hashing; short-lived access tokens; private-network-only database access with no public IP; secrets held in a managed secret store rather than in configuration files; strict tenant scoping so one organization's queries cannot reach another's data; rate limiting on authentication; and least-privilege service accounts.

No system is perfectly secure, and we cannot guarantee absolute security. If we become aware of a breach affecting your personal information, we will notify you and any required regulator as the law directs, without unreasonable delay.

---

## 8. Your choices and rights

**Everyone, regardless of where you live, can:**

- **See and correct** your account information on the Profile page.
- **Turn notifications on or off** by channel in Profile → Notifications.
- **Stop text messages** by replying STOP, or by removing your number.
- **Get a copy** of your personal information by emailing [{CONTACT_PRIVACY}](mailto:{CONTACT_PRIVACY}).
- **Delete your account** by emailing [{CONTACT_PRIVACY}](mailto:{CONTACT_PRIVACY}). Note that records within an organization's workspace, such as who worked which event, belong to that organization and may be retained by it.

**If you live in California, Colorado, Connecticut, Delaware, Iowa, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Texas, Utah, Virginia, or another state with a comprehensive privacy law**, you also have the right to confirm whether we process your personal information, access it in a portable form, correct it, delete it, and opt out of sale, sharing for targeted advertising, or profiling with significant effects. **We do none of those last three**, so there is nothing to opt out of, but the rights are yours regardless.

We will not discriminate against you for exercising any privacy right.

To exercise a right, email [{CONTACT_PRIVACY}](mailto:{CONTACT_PRIVACY}) from the address on your account, or write to us at the address in section 11. We will verify your identity, respond within 45 days, and tell you if we need a further 45 days. An authorized agent may act for you with written permission. If we deny a request you may appeal by replying to our decision, and we will respond to the appeal within 45 days with our reasoning.

**International users.** The Service is operated from the United States and all information is stored there. If you use ProgramRow from outside the United States, you understand your information will be transferred to and processed in the United States, where privacy laws may differ from those in your country.

---

## 9. Links to other sites

Text messages and emails from ProgramRow link only to programrow.com. Our documentation may link to third-party sites we do not control, and this policy does not apply to them.

---

## 10. Changes to this policy

We may update this Privacy Policy. When we do, we will change the "Last updated" date above. If a change materially affects how we use your personal information, we will give notice by email or in the Service **at least 30 days before it takes effect**, so you have time to object or close your account.

We will never apply a materially different use to information already collected without asking you first.

---

## 11. Contact us

**Privacy questions and rights requests:** [{CONTACT_PRIVACY}](mailto:{CONTACT_PRIVACY})
**General support:** [{CONTACT_SUPPORT}](mailto:{CONTACT_SUPPORT})

{LEGAL_ENTITY}
{LEGAL_ADDRESS}

If you contact us about a privacy matter and are not satisfied with our response, you may have the right to complain to your state attorney general or data protection authority.
